import { useMemo, useState } from "react";
import { Package } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  useCreateWoodboxesPrice,
  useDeleteWoodboxesPrice,
  useGetWoodboxPriceByCompanyId,
  useGetWoodboxPriceDefault,
} from "@/api/woodboxPrice.api";
import { UpdateGlobalParameterDto } from "@/types/api/api-types";
import { WoodboxPriceGroup } from "@/types/forms/woodbox.form";
import { OptionsWoodboxItem } from "@/components/backOffice/parameters/options/woodbox/optionsWoodboxItem";
import { OptionsWoodboxModal } from "@/components/backOffice/parameters/options/woodbox/optionsWoodboxModal";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { Button } from "@/components/ui/button";

type ParameterWoodboxProps = {
  companyId: string;
};

export const ParameterWoodBox = ({ companyId }: ParameterWoodboxProps) => {
  const { t } = useTranslation("boClients");
  const { data: woodboxesPriceCompany } = useGetWoodboxPriceByCompanyId(companyId);
  const { data: woodboxesPriceDefault } = useGetWoodboxPriceDefault();
  const { mutateAsync: createWoodboxesPrice, isPending: createWoodboxesPriceIsPending } =
    useCreateWoodboxesPrice();
  const { mutateAsync: deleteWoodboxesPrice, isPending: deleteWoodboxesPriceIsPending } =
    useDeleteWoodboxesPrice();

  const [editWoodBox, setEditWoodBox] = useState<UpdateGlobalParameterDto["woodBoxesPrice"] | null>(
    null,
  );

  const GroupWoodboxes: WoodboxPriceGroup[] = useMemo(() => {
    if (!woodboxesPriceCompany) return [];
    return Object.values(
      woodboxesPriceCompany.reduce(
        (acc, item) => {
          const { region, bottleVolume } = item;
          const groupKey = `${region}-${bottleVolume}`;
          if (!acc[groupKey]) {
            acc[groupKey] = { region, bottleVolume, items: [] };
          }

          acc[groupKey].items.push(item);
          return acc;
        },
        {} as Record<
          string,
          {
            region: string;
            bottleVolume: number;
            items: typeof woodboxesPriceCompany;
          }
        >,
      ),
    )
      .map((group) => ({
        region: group.region,
        bottleVolume: group.bottleVolume,
        items: group.items.sort((a, b) => Number(a.bottleQuantity) - Number(b.bottleQuantity)),
      }))
      .sort((a: WoodboxPriceGroup, b: WoodboxPriceGroup) => {
        const volumeComparison = a.bottleVolume - b.bottleVolume;
        if (volumeComparison === 0) {
          return a.region.localeCompare(b.region);
        }
        return volumeComparison;
      });
  }, [woodboxesPriceCompany]);

  const handleClick = async () => {
    if (GroupWoodboxes.length === 0) {
      if (!woodboxesPriceDefault || woodboxesPriceDefault.length === 0) return;
      await createWoodboxesPrice(
        woodboxesPriceDefault?.map((item) => ({
          bottleVolume: item.bottleVolume,
          bottleQuantity: item.bottleQuantity,
          price: item.price,
          region: item.region,
          globalParametersId: undefined,
          companyId: companyId,
        })),
      );
    } else {
      await deleteWoodboxesPrice(companyId);
    }
  };

  return (
    <div className="flex w-full max-w-[600px] flex-col gap-2 rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-row justify-between gap-4">
        <div className="flex w-fit flex-row items-center gap-4">
          <Package className="text-black" size={24} />
          <div className="flex flex-1 flex-col">
            <h3 className="text-lg font-semibold text-black">{t("woodboxOption.title")}</h3>
          </div>
        </div>
      </div>
      {GroupWoodboxes.map((group: WoodboxPriceGroup, index: number) => (
        <OptionsWoodboxItem
          group={group}
          index={index}
          key={index}
          setEditWoodBox={setEditWoodBox}
        />
      ))}
      <div className="flex w-full items-center justify-center">
        <Button
          type="button"
          variant="tertiary"
          disabled={createWoodboxesPriceIsPending || deleteWoodboxesPriceIsPending}
          onClick={handleClick}
        >
          {createWoodboxesPriceIsPending || deleteWoodboxesPriceIsPending ? (
            <StyledSpinner />
          ) : GroupWoodboxes.length === 0 ? (
            t("woodboxOption.customize")
          ) : (
            t("woodboxOption.delete")
          )}
        </Button>
      </div>
      {editWoodBox && (
        <OptionsWoodboxModal
          closeModal={() => setEditWoodBox(null)}
          title={t("woodboxOption.editModalTitle")}
          woodBoxesPrice={editWoodBox}
        />
      )}
    </div>
  );
};
